/**
 * AssessmentDeliveryShell.jsx — Phase 2B
 * Left-nav workspace layout for Assessment Delivery administration.
 */
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Users, ClipboardCheck, Building2, ShieldCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const NAV_ITEMS = [
  { to: "/assessment-delivery",               label: "Offerings",      icon: LayoutDashboard, exact: true,  testid: "adelnav-offerings"   },
  { to: "/assessment-delivery/registrations", label: "Registrations",  icon: Users,           exact: false, testid: "adelnav-registrations"},
  { to: "/assessment-delivery/venues",        label: "Venues",         icon: Building2,       exact: false, testid: "adelnav-venues"       },
];

/** Admin roles — can see the full delivery admin module */
const ADMIN_ROLES = new Set([
  "super_admin", "institution_admin", "programme_manager", "registrar", "dean",
]);

export function AssessmentDeliveryShell({ children, title, breadcrumb }) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) return null;

  if (!ADMIN_ROLES.has(user.role)) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-8"
           data-testid="adel-access-denied">
        <div className="max-w-md w-full rounded-xl border border-amber-800/50 bg-amber-950/20 p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-amber-900/40 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-6 h-6 text-amber-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-100 mb-2">Access Denied</h2>
          <p className="text-sm text-slate-400 mb-4">
            Assessment Delivery administration requires the{" "}
            <strong className="text-slate-300">registrar</strong> or{" "}
            <strong className="text-slate-300">institution_admin</strong> role.
          </p>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium
                           border border-amber-700/60 bg-amber-950/50 text-amber-300">
            403 Permission Denied
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="assessment-delivery-shell">
      {/* ── Left sub-nav ─────────────────────────────────────────────────── */}
      <aside className="w-52 flex-shrink-0 border-r border-slate-800 bg-slate-900/60 flex flex-col pt-6 pb-8">
        <div className="px-4 mb-6">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            Assessment Delivery
          </span>
        </div>
        <nav className="flex flex-col gap-0.5 px-2">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-emerald-900/40 text-emerald-300 font-medium"
                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
                  }`
                }
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-auto">
        <div className="border-b border-slate-800 bg-slate-900/40 px-8 py-5">
          {breadcrumb && (
            <div className="text-xs text-slate-500 mb-1 flex items-center gap-1">
              <span>Academic Operations</span>
              <span className="mx-1">›</span>
              <span className="text-slate-400">Assessment Delivery</span>
              {breadcrumb && (
                <>
                  <span className="mx-1">›</span>
                  <span className="text-emerald-400">{breadcrumb}</span>
                </>
              )}
            </div>
          )}
          {title && (
            <h1 className="text-xl font-semibold text-slate-100 tracking-tight">{title}</h1>
          )}
        </div>
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}

/** Returns true if user has delivery admin capability */
export function useIsDeliveryAdmin() {
  const { user } = useAuth();
  const ADMIN = new Set(["super_admin", "institution_admin", "programme_manager", "registrar", "dean"]);
  return user ? ADMIN.has(user.role) : false;
}
